import { useState } from 'react';
import { Image as ImageIcon, Trash2, RefreshCw, XCircle } from 'lucide-react';
import { Button, Spinner, Alert } from 'react-bootstrap';
import { SectionHeader } from '../shared/SectionHeader';
import { mediaClient } from '../../../../../../infrastructure/MediaClient';
import type { SkinSeoulAdminProduct } from '../../../../../../models/ProductTypes';

interface PublishedImagePanelPdpProps {
  readonly publishedPdpImages: string[];
  readonly productsBySku: SkinSeoulAdminProduct[];
  readonly imageRefreshKey: number;
  readonly onRefreshImages: () => void;
}

export const PublishedImagePanelPdp = ({
  publishedPdpImages,
  productsBySku,
  imageRefreshKey,
  onRefreshImages,
}: PublishedImagePanelPdpProps) => {
  const [deletingImages, setDeletingImages] = useState<Set<string>>(new Set());
  const [deleteMessage, setDeleteMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [isClearingAll, setIsClearingAll] = useState(false);

  const handleDeletePdpImage = async (imageUrl: string) => {
    const productsWithThisImage = productsBySku.filter(
      (product) => product.learnMoreUrl && product.learnMoreUrl.includes(imageUrl),
    );

    if (productsWithThisImage.length === 0) {
      setDeleteMessage({ type: 'error', text: 'No products found with this PDP image' });
      return;
    }

    setDeletingImages((prev) => new Set(prev).add(imageUrl));
    setDeleteMessage(null);

    try {
      let successCount = 0;
      let failedCount = 0;

      for (const product of productsWithThisImage) {
        if (!product.sku) {
          continue;
        }

        const response = await mediaClient.publishLearnMoreImages({
          sku: product.sku,
          toDelete: [imageUrl],
        });

        if (response.success) {
          successCount++;
        } else {
          failedCount++;
          console.error(`Failed to delete PDP image from SKU ${product.sku}:`, response.message);
        }
      }

      if (failedCount === 0) {
        setDeleteMessage({
          type: 'success',
          text: `PDP image deleted from ${successCount} SKU(s) successfully!`,
        });
      } else {
        setDeleteMessage({
          type: 'error',
          text: `Partial success: ${successCount} succeeded, ${failedCount} failed`,
        });
      }

      setTimeout(() => {
        onRefreshImages();
        setDeleteMessage(null);
      }, 1500);
    } catch (error) {
      const errorMsg = error instanceof Error ? error.message : 'Unknown error';
      setDeleteMessage({ type: 'error', text: `Error: ${errorMsg}` });
    } finally {
      setDeletingImages((prev) => {
        const updated = new Set(prev);
        updated.delete(imageUrl);
        return updated;
      });
    }
  };

  const handleClearAllPdpImages = async () => {
    if (!confirm('Are you sure you want to clear all PDP images from all SKUs? This action cannot be undone.')) {
      return;
    }

    setIsClearingAll(true);
    setDeleteMessage(null);

    try {
      let successCount = 0;
      let failedCount = 0;

      for (const product of productsBySku) {
        if (!product.sku) {
          continue;
        }

        const response = await mediaClient.publishLearnMoreImages({
          sku: product.sku,
          clearAll: true,
        });

        if (response.success) {
          successCount++;
        } else {
          failedCount++;
          console.error(`Failed to clear PDP images from SKU ${product.sku}:`, response.message);
        }
      }

      if (failedCount === 0) {
        setDeleteMessage({
          type: 'success',
          text: `All PDP images cleared from ${successCount} SKU(s) successfully!`,
        });
      } else {
        setDeleteMessage({
          type: 'error',
          text: `Partial success: ${successCount} succeeded, ${failedCount} failed`,
        });
      }

      setTimeout(() => {
        onRefreshImages();
        setDeleteMessage(null);
      }, 1500);
    } catch (error) {
      const errorMsg = error instanceof Error ? error.message : 'Unknown error';
      setDeleteMessage({ type: 'error', text: `Error: ${errorMsg}` });
    } finally {
      setIsClearingAll(false);
    }
  };

  return (
    <div className="mb-4">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
        <SectionHeader title="Published PDP Images" />
        <div style={{ display: 'flex', gap: '8px' }}>
          <Button
            variant="outline-primary"
            size="sm"
            onClick={onRefreshImages}
            style={{ display: 'flex', alignItems: 'center', gap: '4px', padding: '4px' }}
          >
            <RefreshCw size={12} />
            <span style={{ fontSize: '0.75rem' }}>Refresh</span>
          </Button>
          {publishedPdpImages.length > 0 && (
            <Button
              variant="outline-danger"
              size="sm"
              onClick={handleClearAllPdpImages}
              disabled={isClearingAll}
              style={{ display: 'flex', alignItems: 'center', gap: '4px', padding: '4px' }}
            >
              {isClearingAll ? (
                <>
                  <Spinner
                    as="span"
                    animation="border"
                    size="sm"
                    role="status"
                    aria-hidden="true"
                    style={{ width: '10px', height: '10px' }}
                  />
                  <span style={{ fontSize: '0.75rem' }}>Clearing...</span>
                </>
              ) : (
                <>
                  <XCircle size={12} />
                  <span style={{ fontSize: '0.75rem' }}>Clear All</span>
                </>
              )}
            </Button>
          )}
        </div>
      </div>

      {deleteMessage && (
        <Alert
          variant={deleteMessage.type === 'success' ? 'success' : 'danger'}
          dismissible
          onClose={() => setDeleteMessage(null)}
          style={{ fontSize: '0.85rem', padding: '8px 12px', marginBottom: '12px' }}
        >
          {deleteMessage.text}
        </Alert>
      )}

      {publishedPdpImages.length === 0 ? (
        <div
          style={{
            padding: '40px 20px',
            textAlign: 'center',
            background: 'white',
            borderRadius: '8px',
            border: '2px dashed #dee2e6',
          }}
        >
          <ImageIcon size={48} className="mb-3 opacity-25" />
          <p className="mb-0 text-muted">No PDP images published yet</p>
          <small className="text-muted">Publish images from the editor</small>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {publishedPdpImages.map((url, index) => {
            const isDeleting = deletingImages.has(url);
            return (
              <div
                key={`${url}-${index}-${imageRefreshKey}`}
                style={{
                  background: 'white',
                  borderRadius: '8px',
                  overflow: 'hidden',
                  border: '1px solid #dee2e6',
                  transition: 'all 0.2s',
                  position: 'relative',
                  opacity: isDeleting ? 0.5 : 1,
                }}
              >
                <div
                  style={{
                    width: '100%',
                    overflow: 'hidden',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: '#f8f9fa',
                  }}
                >
                  <img
                    src={`${url}?v=${imageRefreshKey}`}
                    alt={`Published PDP image`}
                    style={{
                      maxWidth: '100%',
                      height: 'auto',
                      display: 'block',
                    }}
                    loading="lazy"
                  />
                  <Button
                    variant="danger"
                    size="sm"
                    onClick={() => handleDeletePdpImage(url)}
                    disabled={isDeleting}
                    style={{
                      position: 'absolute',
                      top: '8px',
                      right: '8px',
                      padding: '4px 8px',
                      fontSize: '0.7rem',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px',
                    }}
                  >
                    {isDeleting ? (
                      <>
                        <Spinner
                          as="span"
                          animation="border"
                          size="sm"
                          role="status"
                          aria-hidden="true"
                          style={{ width: '10px', height: '10px' }}
                        />
                        <span>Deleting</span>
                      </>
                    ) : (
                      <>
                        <Trash2 size={10} />
                        <span>Delete</span>
                      </>
                    )}
                  </Button>
                </div>
                <div
                  style={{
                    fontSize: '0.7rem',
                    color: '#6c757d',
                    fontFamily: 'monospace',
                    wordBreak: 'break-all',
                    padding: '8px',
                  }}
                >
                  {url}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

